import { ThemeEvents } from '@theme/events';

/**
 * Quantity-break cards on the product page.
 *
 * Picking a card drives the theme's own quantity selector, so the add-to-cart
 * form stays the single source of truth for the quantity. The cards only
 * display prices; the discount itself comes from Shopify's automatic discounts.
 */
class VmQuantityBreaks extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onPick, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    this.#scope()?.addEventListener(ThemeEvents.variantUpdate, this.#onVariant, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #scope() {
    return this.closest('.shopify-section, dialog');
  }

  #selector() {
    return this.#scope()?.querySelector('quantity-selector-component') ?? null;
  }

  #quantityInput() {
    const input = this.#selector()?.querySelector('input[name=quantity]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** A card was picked: push its quantity into the theme's quantity selector. */
  #onPick = (event) => {
    const picked = event.target;
    if (!(picked instanceof HTMLInputElement) || picked.type !== 'radio') return;

    const selector = this.#selector();
    const input = this.#quantityInput();
    if (!selector || !input) return;

    if (typeof selector.setValue === 'function' && typeof selector.onQuantityChange === 'function') {
      selector.setValue(picked.value);
      selector.onQuantityChange();
      if (typeof selector.updateButtonStates === 'function') selector.updateButtonStates();
    } else {
      input.value = picked.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /** The quantity changed elsewhere (plus/minus buttons): reflect it on the cards. */
  #onQuantity = (event) => {
    const scope = this.#scope();
    if (!scope || !(event.target instanceof Node) || !scope.contains(event.target)) return;
    this.#sync();
  };

  /** Another variant was selected: take the freshly rendered prices from the new HTML. */
  #onVariant = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const fresh = data.html.querySelector(`vm-quantity-breaks[data-block-id="${this.dataset.blockId}"]`);
    if (!fresh) return;

    this.hidden = fresh.hasAttribute('hidden');
    this.replaceChildren(...fresh.cloneNode(true).childNodes);
    this.#sync();
  };

  #sync() {
    const input = this.#quantityInput();
    const quantity = input ? parseInt(input.value) || 1 : 1;

    for (const radio of this.querySelectorAll('input[type=radio]')) {
      if (radio instanceof HTMLInputElement) radio.checked = parseInt(radio.value) === quantity;
    }
  }
}

if (!customElements.get('vm-quantity-breaks')) {
  customElements.define('vm-quantity-breaks', VmQuantityBreaks);
}
